import { ApiError, NetworkError, toApiError } from './errors';

export interface HttpClientOptions {
  /** e.g. "http://localhost:3001/v1" — includes the API version segment. */
  baseUrl: string;
  /**
   * Returns the current bearer token, or null when signed out.
   *
   * A function, not a value, because the token changes over the session and
   * this package must never own or import the auth client — admin and platform
   * have separate better-auth instances and separate token stores.
   */
  getToken: () => string | null;
  /** Called on any 401. Wire this to "clear session + redirect to login". */
  onUnauthorized?: () => void;
  /** Extra headers applied to every request (correlation ids, locale, ...). */
  defaultHeaders?: () => Record<string, string>;
}

export type HttpClient = <TResponse>(url: string, init?: RequestInit) => Promise<TResponse>;

/**
 * Builds the app's single fetch entry point.
 *
 * Deliberately shaped to match Orval's custom-mutator signature so the
 * generated API client routes through here — every request gets the same auth
 * header, error normalization, and 401 handling with no per-endpoint wiring.
 */
export function createHttpClient(options: HttpClientOptions): HttpClient {
  const { baseUrl, getToken, onUnauthorized, defaultHeaders } = options;
  const normalizedBase = baseUrl.replace(/\/$/, '');

  return async function request<TResponse>(
    url: string,
    init: RequestInit = {},
  ): Promise<TResponse> {
    const token = getToken();

    const headers = new Headers(defaultHeaders?.() ?? {});
    for (const [key, value] of new Headers(init.headers).entries()) {
      headers.set(key, value);
    }
    if (token) headers.set('Authorization', `Bearer ${token}`);
    if (init.body !== undefined && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    const target = url.startsWith('http') ? url : `${normalizedBase}${url}`;

    let response: Response;
    try {
      response = await fetch(target, { ...init, headers });
    } catch (cause) {
      throw new NetworkError(cause);
    }

    if (response.status === 401) {
      onUnauthorized?.();
      throw toApiError(401, await readBody(response));
    }

    if (!response.ok) {
      throw toApiError(response.status, await readBody(response));
    }

    if (response.status === 204 || response.headers.get('Content-Length') === '0') {
      return undefined as TResponse;
    }

    return (await readBody(response)) as TResponse;
  };
}

async function readBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('Content-Type') ?? '';
  try {
    if (contentType.includes('application/json')) return await response.json();
    const text = await response.text();
    return text.length > 0 ? text : null;
  } catch {
    return null;
  }
}

/**
 * Default TanStack Query retry policy.
 *
 * Retrying a 4xx is pointless and, on auth endpoints, actively harmful — the
 * backend rate-limits login and password reset more strictly than everything
 * else, so blind retries can lock a user out of their own account.
 */
export function shouldRetryRequest(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.isClientError) return false;
  return failureCount < 2;
}
